import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ChevronDown, LogOut, Search, Settings, X } from 'lucide-react';
import { toast } from 'sonner';
import { signOut, useSession } from '@/api/auth';
import { useIsAdmin } from '@/api/hooks';
import { usePermissions } from '@/api/permissions';
import { ThemeToggle } from '@/components/ThemeToggle';
import { UserAvatar } from '@/components/UserAvatar';
import { ClientPicker } from '@/components/shell/ClientPicker';
import { ClientModeSection } from '@/components/shell/ClientModeSection';
import { BookmarksSection } from '@/components/shell/Bookmarks';
import { MODULES } from '@/lib/modules';
import { MODULE_MENUS, isNavLink, isView, moduleForPath, type ModuleId } from '@/lib/navigation';
import { filterModuleMenu } from '@/lib/permissions';
import { pageDestinations, searchPages } from '@/lib/pageSearch';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * The phone's "More" (Mobile.md §3): one sheet for everything that isn't a tab. Jump-to page search, the client
 * picker (+ client-mode links while scoped), every module the user's role can open (RBAC: `filterModuleMenu`), with
 * pages that have no phone layout tagged "Desktop", bookmarks, then Settings, theme and sign out (moved out of the
 * crowded phone header). Replaces the old hamburger sidebar sheet.
 */
export function MoreSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 md:hidden" />
        <DialogPrimitive.Content aria-describedby={undefined}
          className="fixed inset-x-0 bottom-0 top-[6dvh] z-50 flex flex-col rounded-t-2xl border-t border-border bg-background shadow-2xl focus:outline-none md:hidden">
          {open && <MoreBody onClose={() => onOpenChange(false)} />}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

const rowCls = 'flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground active:bg-muted';

function MoreBody({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const perms = usePermissions();
  const { data: isAdmin } = useIsAdmin();
  const { data: session } = useSession();
  const userName = (session?.user as { name?: string } | undefined)?.name ?? session?.user?.email ?? '';
  const [q, setQ] = useState('');
  const current = moduleForPath(window.location.pathname);
  const [open, setOpen] = useState<Set<string>>(() => new Set(current ? [current] : []));

  const destinations = useMemo(() => pageDestinations(!!isAdmin, perms), [isAdmin, perms]);
  const results = useMemo(() => searchPages(destinations, q, 10), [destinations, q]);
  const modules = useMemo(() => MODULES.filter((m) => m.status === 'live')
    .map((m) => ({ m, groups: filterModuleMenu(m.id as ModuleId, MODULE_MENUS[m.id as ModuleId] ?? [], perms) }))
    .filter((x) => x.groups.length), [perms]);

  const doSignOut = async () => {
    await signOut();
    toast.success('Signed out');
    window.location.href = ROUTES.login;
  };

  return (
    <>
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        <UserAvatar name={userName || '?'} size={32} />
        <div className="min-w-0 flex-1">
          <DialogPrimitive.Title className="truncate text-sm font-medium text-foreground">{userName}</DialogPrimitive.Title>
          <div className="text-[11px] text-muted-foreground">{perms?.viewAs ? `Viewing as ${perms.viewAs.roleName}` : perms?.roleName ?? 'Cosmos'}</div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-2 text-muted-foreground active:bg-muted"><X className="h-5 w-5" /></button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Jump to a page" aria-label="Jump to a page"
            className="h-11 w-full rounded-lg border border-border bg-card pl-9 pr-3 text-base text-foreground outline-none placeholder:text-muted-foreground focus:border-ring" />
        </div>

        {q.trim() ? (
          <ul className="mt-2">
            {results.map((r) => (
              <li key={`${r.to}|${r.label}`}>
                <button type="button" className={cn(rowCls, 'w-full text-left')} onClick={() => navigate(r.to)}>
                  <r.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate">{r.label}</span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">{r.group}</span>
                </button>
              </li>
            ))}
            {!results.length && <li className="px-3 py-6 text-center text-sm text-muted-foreground">No pages match.</li>}
          </ul>
        ) : (<>
          <section className="mt-4">
            <h2 className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Client</h2>
            <div className="px-1"><ClientPicker /></div>
            <div className="mt-2"><ClientModeSection onNavigate={onClose} /></div>
          </section>

          <section className="mt-4">
            <h2 className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Modules</h2>
            {modules.map(({ m, groups }) => {
              const isOpen = open.has(m.id);
              return (
                <div key={m.id}>
                  <button type="button" aria-expanded={isOpen} className={cn(rowCls, 'w-full text-left font-medium')}
                    onClick={() => setOpen((s) => { const n = new Set(s); if (n.has(m.id)) n.delete(m.id); else n.add(m.id); return n; })}>
                    <m.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="flex-1">{m.label}</span>
                    <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', !isOpen && '-rotate-90')} />
                  </button>
                  {isOpen && (
                    <ul className="mb-1 ml-9 border-l border-divider pl-2">
                      {groups.flatMap((g) => g.items.filter(isNavLink).filter((i) => !isView(i))).map((i) => (
                        <li key={i.to}>
                          <Link to={i.to} onClick={onClose} className={cn(rowCls, 'py-2')}>
                            <span className="min-w-0 flex-1 truncate">{i.label}</span>
                            {!i.mobile && <span className="shrink-0 rounded bg-muted px-1.5 py-px text-[10px] font-medium text-muted-foreground" title="No phone layout yet">Desktop</span>}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </section>

          <section className="mt-4">
            <BookmarksSection rowClass={rowCls} onNavigate={onClose} />
          </section>

          <section className="mt-4 border-t border-divider pt-3">
            <Link to={ROUTES.account.info} onClick={onClose} className={rowCls}><Settings className="h-4 w-4 text-muted-foreground" /> Settings</Link>
            <div className={cn(rowCls, 'justify-between')}><span>Theme</span><ThemeToggle /></div>
            <button type="button" onClick={doSignOut} className={cn(rowCls, 'w-full text-left text-destructive')}><LogOut className="h-4 w-4" /> Sign out</button>
          </section>
        </>)}
      </div>
    </>
  );
}
